import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { IoCloseCircle, IoDownloadOutline, IoTrashOutline } from 'react-icons/io5';
import { formatIstDateTime } from '../utils/dateFormat';

import type { FriendEntry } from '../App';

interface GalleryProps {
  currentUserId: string;
  selectedFriend: FriendEntry | null;
}

interface MediaItem {
  id: number;
  messageId: number;
  imageDataUrl: string | null;
  createdAtUtc: string;
  fromUserId?: number;
}

const PAGE_SIZE = 15;

const Gallery: React.FC<GalleryProps> = ({ currentUserId, selectedFriend }) => {
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<MediaItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  const otherUserId = selectedFriend ? String(selectedFriend.id) : '';
  const skipRef = useRef(0);
  const loadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  const loadMore = useCallback(async () => {
    if (!currentUserId || !otherUserId || loadingRef.current || !hasMoreRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      const currentSkip = skipRef.current;
      const { data } = await axiosInstance.get('/api/v2/media/gallery', {
        params: {
          userB: otherUserId,
          skip: currentSkip,
          take: PAGE_SIZE,
        },
      });

      if (!Array.isArray(data)) {
        console.error('Invalid response from gallery API:', data);
        throw new Error('Unexpected response from gallery service');
      }

      const filteredItems: MediaItem[] = data.filter((img: MediaItem) =>
        img.imageDataUrl &&
        img.imageDataUrl.startsWith('data:image/') &&
        img.imageDataUrl.length > 0
      );

      setMedia((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const merged = [...prev];
        for (const item of filteredItems) {
          if (!existingIds.has(item.id)) {
            merged.push(item);
          }
        }
        return merged;
      });

      const receivedCount = data.length;
      const nextSkip = currentSkip + receivedCount;
      skipRef.current = nextSkip;

      const moreAvailable = receivedCount === PAGE_SIZE;
      hasMoreRef.current = moreAvailable;
      setHasMore(moreAvailable);
      setError(null);
    } catch (err: any) {
      console.error('Error loading gallery:', err);
      const errorMessage = err?.response?.data?.message || err?.message || 'Failed to load gallery';
      setError(errorMessage);
      hasMoreRef.current = false;
      setHasMore(false);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [currentUserId, otherUserId]);

  const canDeleteSelected = selected
    ? selected.fromUserId == null || selected.fromUserId === parseInt(currentUserId)
    : false;

  const deleteSelected = useCallback(async () => {
    if (!selected) return;
    if (deleting) return;
    if (!canDeleteSelected) {
      alert('Only the sender can delete this image.');
      return;
    }

    const ok = window.confirm('Delete this image?');
    if (!ok) return;

    setDeleting(true);
    try {
      await axiosInstance.delete(`/api/messages/${selected.messageId}`);

      // Optimistic UI update
      setMedia((prev) => prev.filter((m) => m.messageId !== selected.messageId));
      setSelected(null);
    } catch (err: any) {
      console.error('Failed to delete image message:', err);
      alert(err?.response?.data?.message || err?.message || 'Failed to delete image');
    } finally {
      setDeleting(false);
    }
  }, [selected, deleting, canDeleteSelected]);

  const handleRetry = useCallback(() => {
    hasMoreRef.current = true;
    setHasMore(true);
    setError(null);
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    setMedia([]);
    skipRef.current = 0;
    hasMoreRef.current = true;
    setHasMore(true);
    setError(null);
    loadingRef.current = false;
  }, [currentUserId, otherUserId]);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  useEffect(() => {
    const container = containerRef.current;
    const sentinel = sentinelRef.current;
    if (!container || !sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting) {
          loadMore();
        }
      },
      {
        root: container,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [loadMore]);

  if (!selectedFriend) {
    return (
      <div className="flex flex-col h-screen bg-[#071311] text-slate-100">
        <div className="border-b border-emerald-300/15 bg-[#0f2a2d] px-4 py-4 shadow-lg shadow-black/20">
          <h1 className="text-2xl font-bold text-white">Gallery</h1>
          <p className="text-sm text-emerald-100/70">Shared images</p>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 text-center text-emerald-100/65">
          Select a friend from Chat to view shared images.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-[#071311] text-slate-100">
      <div className="border-b border-emerald-300/15 bg-[#0f2a2d] px-4 py-4 shadow-lg shadow-black/20">
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Gallery</h1>
            <p className="text-sm text-emerald-100/70">Shared images with {selectedFriend.name}</p>
          </div>
          {media.length > 0 && (
            <div className="rounded-full border border-cyan-300/25 bg-cyan-300/10 px-3 py-1 text-xs font-semibold text-cyan-100">
              {media.length}
            </div>
          )}
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto bg-[radial-gradient(circle_at_top_right,rgba(45,212,191,0.16),transparent_34%),linear-gradient(180deg,#071311_0%,#081016_100%)] p-3">
        {error && (
          <div className="mb-4 rounded-lg bg-rose-500/10 border border-rose-400/45 text-rose-100 px-4 py-3 text-sm flex items-center justify-between gap-4">
            <span>{error}</span>
            <button
              onClick={handleRetry}
              className="shrink-0 rounded-md border border-rose-300/80 px-3 py-1 text-xs font-medium text-rose-50 hover:bg-rose-400/20 transition"
            >
              Retry
            </button>
          </div>
        )}
        {media.length === 0 && loading ? (
          <div className="flex items-center justify-center h-full text-emerald-100/65">Loading...</div>
        ) : media.length === 0 ? (
          <div className="flex items-center justify-center h-full text-emerald-100/65">No images shared yet</div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {media.map((item) => (
              <button
                key={item.id}
                className="aspect-square overflow-hidden rounded-lg border border-white/10 bg-[#102322] shadow-md shadow-black/20 transition hover:border-cyan-200/60 hover:brightness-110"
                onClick={() => setSelected(item)}
                title={formatIstDateTime(item.createdAtUtc)}
              >
                {item.imageDataUrl ? (
                  <img
                    src={item.imageDataUrl}
                    alt="Gallery item"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-emerald-100/45 text-xs">
                    No preview
                  </div>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="py-4 text-center text-sm text-emerald-100/55">
          {loading && <span>Loading...</span>}
          {!loading && !hasMore && media.length > 0 && <span>End of results</span>}
        </div>

        <div ref={sentinelRef} className="h-1 w-full" />
      </div>

      {selected && (
        <div className="fixed inset-0 bg-[#020706]/95 z-50 flex items-center justify-center p-4">
          <div className="absolute top-4 right-4 flex items-center gap-3 z-10">
            <button
              onClick={deleteSelected}
              disabled={deleting || !canDeleteSelected}
              className={`text-white rounded-full p-2 transition ${
                deleting
                  ? 'bg-red-900 opacity-70 cursor-not-allowed'
                  : canDeleteSelected
                  ? 'bg-red-600 hover:bg-red-500'
                  : 'bg-gray-700 opacity-60 cursor-not-allowed'
              }`}
              title={canDeleteSelected ? 'Delete' : 'You can only delete images you sent'}
            >
              <IoTrashOutline size={26} />
            </button>
            {selected.imageDataUrl && (
              <a
                href={selected.imageDataUrl}
                download={`image-${selected.id}.jpg`}
                className="text-white bg-cyan-600 rounded-full p-2 hover:bg-cyan-500"
                title="Download"
              >
                <IoDownloadOutline size={28} />
              </a>
            )}
            <button
              onClick={() => setSelected(null)}
              className="text-white bg-red-500 rounded-full p-2 hover:bg-red-400"
              title="Close"
            >
              <IoCloseCircle size={32} />
            </button>
          </div>
          <img
            src={selected.imageDataUrl ?? ''}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={() => setSelected(null)}
          />
        </div>
      )}
    </div>
  );
};

export default Gallery;
